import { useCallback } from 'react';

import { type Task } from '@air/classes-task-scheduler';
import { largeUploadByIdSelector } from '@air/redux-uploader';
import { getLargeFileChunks, useCanLargeChunkBeUploaded, useLargeUploadCanBeCompleted } from '@air/upload-utils';

import { type UploadTaskParams } from '~/components/Upload/hooks/types';
import { useCompleteLargePublicUpload } from '~/components/Upload/publicUpload/useCompleteLargePublicUpload';
import { useCreateLargePublicUpload } from '~/components/Upload/publicUpload/useCreateLargePublicUpload';
import { useUploadLargePublicFileWithRetries } from '~/components/Upload/publicUpload/useUploadLargePublicFileWithRetries';
import { useAirStore } from '~/utils/ReduxUtils';

export const useGetLargePublicUploadTasks = () => {
  const store = useAirStore();
  const { createLargePublicUpload } = useCreateLargePublicUpload();
  const { largeUploadCanBeCompleted } = useLargeUploadCanBeCompleted();
  const { completeLargePublicUpload } = useCompleteLargePublicUpload();
  const { canLargeChunkBeUploaded } = useCanLargeChunkBeUploaded();
  const { uploadLargePublicFileWithRetries } = useUploadLargePublicFileWithRetries();

  const getStartLargeUploadTask = useCallback(
    (params: UploadTaskParams): Task => ({
      id: `${params.uploadId}-start`,
      run: () => createLargePublicUpload(params),
    }),
    [createLargePublicUpload],
  );

  const getCompleteLargeUploadTask = useCallback(
    ({ uploadId, onUploadFinished, onError }: UploadTaskParams): Task => {
      return {
        id: `${uploadId}-complete`,
        getIsBlocked: () => !largeUploadCanBeCompleted(uploadId),
        run: () => completeLargePublicUpload({ uploadId, onUploadFinished, onError }),
      };
    },
    [completeLargePublicUpload, largeUploadCanBeCompleted],
  );

  const getChunkUploadTasks = useCallback(
    (params: UploadTaskParams): Task[] => {
      const getIsBlocked = () => !canLargeChunkBeUploaded(params.uploadId);

      const upload = largeUploadByIdSelector(store.getState(), params.uploadId);

      if (upload) {
        const fileChunks = getLargeFileChunks(upload);

        return fileChunks.map(
          (chunk, index): Task => ({
            id: `${params.uploadId}#${index}`,
            getIsBlocked,
            run: () =>
              uploadLargePublicFileWithRetries({
                chunk,
                onError: params.onError,
                partNumber: index + 1,
                uploadId: params.uploadId,
              }),
          }),
        );
      }

      return [];
    },
    [canLargeChunkBeUploaded, store, uploadLargePublicFileWithRetries],
  );

  const getLargeUploadTasks = useCallback(
    (params: UploadTaskParams): Task[] => {
      return [getStartLargeUploadTask(params), ...getChunkUploadTasks(params), getCompleteLargeUploadTask(params)];
    },
    [getChunkUploadTasks, getCompleteLargeUploadTask, getStartLargeUploadTask],
  );

  return {
    getLargeUploadTasks,
  };
};
